import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { BarChart3, CheckCircle2, FileSpreadsheet, RefreshCw, Search, Store, TrendingUp, WalletCards } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminStoreReport,
  AdminStoreReportAmountRow,
  AdminStoreReportMetrics,
  AdminUser,
  exportAdminStoreReport,
  getAdminStoreReport,
  getCurrentAdmin,
  logout,
} from "./api";

const year = new Date().getFullYear();
const defaultDateFrom = `${year}-01-01`;
const defaultDateTo = `${year}-12-31`;

function money(value?: number | null) {
  return `NT$ ${Number(value || 0).toLocaleString("zh-TW")}`;
}

function count(value?: number | null) {
  return `${Number(value || 0).toLocaleString("zh-TW")} 筆`;
}

function MetricCard({ icon, label, note, value, tone = "default" }: { icon: ReactNode; label: string; note?: string; value: string; tone?: "default" | "highlight" | "warning" }) {
  const toneClass =
    tone === "highlight"
      ? "border-[#b7d3c1] border-l-[#315746]"
      : tone === "warning"
        ? "border-[#ead8ad] border-l-[#b7791f]"
        : "border-[#dfe6e1] border-l-[#dfe6e1]";
  const iconClass = tone === "warning" ? "bg-[#fff5df] text-[#8a5b14]" : "bg-[#edf5f0] text-[#315746]";
  return (
    <div className={`rounded-md border border-l-4 bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)] ${toneClass}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-bold text-[#69776f]">{label}</p>
          <p className="mt-2 break-words text-[28px] font-extrabold leading-tight text-[#203d32]">{value}</p>
        </div>
        <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${iconClass}`}>{icon}</span>
      </div>
      {note ? <p className="mt-3 truncate text-sm font-semibold text-[#7b877f]">{note}</p> : null}
    </div>
  );
}

function Summary({ metrics }: { metrics: AdminStoreReportMetrics }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <MetricCard icon={<TrendingUp size={22} />} label="成交總額" value={money(metrics.TotalAmount)} note={`平均客單 ${money(metrics.AverageOrderAmount)}`} tone="highlight" />
      <MetricCard icon={<WalletCards size={22} />} label="本期實收" value={money(metrics.PaymentReceived)} note={`帳面已收 ${money(metrics.PaidAmount)}`} />
      <MetricCard icon={<WalletCards size={22} />} label="尾款待收" value={money(metrics.BalanceAmount)} note={`${count(metrics.BalanceDueOrders)}待結清`} tone={metrics.BalanceAmount > 0 ? "warning" : "default"} />
      <MetricCard icon={<Store size={22} />} label="訂單總數" value={count(metrics.OrderCount)} note={`已交付 ${count(metrics.CompletedOrders)}`} />
      <MetricCard icon={<CheckCircle2 size={22} />} label="可交付流程" value={count(metrics.DeliverableOrders)} note="已到店或可通知取件" />
      <MetricCard icon={<BarChart3 size={22} />} label="服務紀錄" value={count(metrics.ServiceRecordCount)} note="依服務日期統計" />
      <MetricCard icon={<BarChart3 size={22} />} label="回訪建立" value={count(metrics.FollowUpCreated)} note={`完成 ${count(metrics.FollowUpCompleted)}`} />
      <MetricCard icon={<CheckCircle2 size={22} />} label="回訪完成率" value={`${metrics.FollowUpCompletionRate || 0}%`} />
    </div>
  );
}

function Breakdown({ title, rows, amountKey }: { title: string; rows: AdminStoreReportAmountRow[]; amountKey: "Amount" | "TotalAmount" }) {
  const max = Math.max(1, ...rows.map((row) => Number(row[amountKey] || 0)));
  return (
    <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
      <h2 className="mb-4 text-base font-extrabold text-[#203d32]">{title}</h2>
      {rows.length === 0 ? <p className="rounded-md border border-dashed border-[#cfd8d2] p-6 text-center font-semibold text-[#7b877f]">目前區間沒有資料</p> : null}
      <div className="space-y-3">
        {rows.map((row) => {
          const amount = Number(row[amountKey] || 0);
          return (
            <div key={row.Key}>
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 break-words font-bold text-[#203d32]">{row.Label}</span>
                <span className="shrink-0 text-[#59665f]">{count(row.Count)}</span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#edf0ee]">
                <div className="h-full rounded-full bg-[#315746]" style={{ width: `${Math.max(8, Math.round((amount / max) * 100))}%` }} />
              </div>
              <p className="mt-1 text-sm font-bold text-[#7b877f]">{money(amount)}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function AdminStoreReports() {
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [report, setReport] = useState<AdminStoreReport | null>(null);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState("");

  const selectedCount = locationIds.length;
  const allSelected = useMemo(() => report ? selectedCount === 0 || selectedCount === report.Locations.length : false, [report, selectedCount]);

  async function load(filters: { DateFrom?: string; DateTo?: string; LocationIds?: string[] } = {}) {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentAdmin();
      setUser(me.User);
      const data = await getAdminStoreReport({ DateFrom: filters.DateFrom ?? dateFrom, DateTo: filters.DateTo ?? dateTo, LocationIds: filters.LocationIds ?? locationIds });
      setReport(data);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "門市報表載入失敗。");
      if (String(error instanceof Error ? error.message : "").includes("登入")) navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void load({ DateFrom: dateFrom, DateTo: dateTo, LocationIds: locationIds });
  }

  function handleReset() {
    setDateFrom(defaultDateFrom);
    setDateTo(defaultDateTo);
    setLocationIds([]);
    void load({ DateFrom: defaultDateFrom, DateTo: defaultDateTo, LocationIds: [] });
  }

  async function handleExport() {
    setExporting(true);
    setMessage("");
    try {
      await exportAdminStoreReport({ DateFrom: report?.DateFrom || dateFrom, DateTo: report?.DateTo || dateTo, LocationIds: locationIds });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "匯出失敗，請稍後再試。");
    } finally {
      setExporting(false);
    }
  }

  function toggleLocation(locationId: string) {
    setLocationIds((current) => {
      if (!report) return current;
      if (current.length === 0) {
        return report.Locations.map((location) => location.LocationId).filter((id) => id !== locationId);
      }
      return current.includes(locationId) ? current.filter((id) => id !== locationId) : [...current, locationId];
    });
  }

  return (
    <AdminLayout sectionLabel="門市管理" title="門市報表" user={user} onLogout={handleLogout}>
      <form className="mb-5 rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={handleSearch}>
        <div className="flex flex-col gap-4 p-4 2xl:flex-row 2xl:items-end 2xl:justify-between">
          <div className="grid min-w-0 flex-1 gap-4 sm:grid-cols-2 2xl:max-w-[560px]">
            <label className="min-w-0">
              <span className="mb-2 block text-sm font-bold text-[#59665f]">起日</span>
              <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
            </label>
            <label className="min-w-0">
              <span className="mb-2 block text-sm font-bold text-[#59665f]">迄日</span>
              <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
            </label>
          </div>

          <div className="rwd-action-grid 2xl:shrink-0">
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:opacity-60" disabled={loading} type="submit">
              <Search size={18} />
              查詢
            </button>
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:opacity-60" disabled={loading} onClick={handleReset} type="button">
              <RefreshCw size={18} />
              重整
            </button>
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:opacity-60" disabled={!report || exporting} onClick={handleExport} type="button">
              <FileSpreadsheet size={18} />
              {exporting ? "匯出中" : "匯出 Excel"}
            </button>
          </div>
        </div>

        <div className="border-t border-[#edf0ee] px-4 py-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-extrabold text-[#203d32]">門市篩選</p>
              <p className="mt-1 text-xs font-semibold text-[#7b877f]">未指定時會統計全部門市；勾選後只看指定門市。</p>
            </div>
            <div className="flex items-center gap-3 text-sm font-bold">
              <span className="text-[#7b877f]">{allSelected ? "全部門市" : `已選 ${selectedCount} 間`}</span>
              <button className="rounded-md border border-[#cfd8d2] px-3 py-1.5 text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => setLocationIds([])} type="button">全選</button>
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
            {report?.Locations.map((location) => {
              const checked = locationIds.length === 0 || locationIds.includes(location.LocationId);
              return (
                <label className={`inline-flex h-11 min-w-0 items-center gap-2 rounded-md border px-3 font-bold transition ${checked ? "border-[#9ec9ad] bg-[#edf8f1] text-[#203d32]" : "border-[#dfe6e1] bg-white text-[#59665f] hover:bg-[#f8faf9]"}`} key={location.LocationId}>
                  <input className="h-4 w-4 shrink-0 accent-[#315746]" checked={checked} onChange={() => toggleLocation(location.LocationId)} type="checkbox" />
                  <span className="truncate">{location.Name}</span>
                </label>
              );
            })}
          </div>
        </div>
      </form>

      {message ? <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-bold text-[#8a1f1f]">{message}</div> : null}

      {loading && !report ? <div className="rounded-md border border-[#dfe6e1] bg-white p-8 text-center text-[#59665f]">載入中...</div> : null}

      {report ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-[#7b877f]">統計區間</p>
              <h2 className="mt-1 text-xl font-extrabold text-[#203d32]">{report.DateFrom} 至 {report.DateTo}</h2>
            </div>
            <p className="rounded-full bg-[#edf5f0] px-4 py-2 text-sm font-bold text-[#315746]">{allSelected ? "全部門市" : `${selectedCount} 間門市`}</p>
          </div>

          <Summary metrics={report.Metrics} />

          <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-extrabold text-[#203d32]">門市排行</h2>
              <span className="text-sm font-bold text-[#7b877f]">依成交總額排序</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[860px] w-full text-left text-sm">
                <thead className="bg-[#f3f7f4] text-[#59665f]">
                  <tr>
                    <th className="px-3 py-3">門市</th>
                    <th className="px-3 py-3 text-right">訂單</th>
                    <th className="px-3 py-3 text-right">成交總額</th>
                    <th className="px-3 py-3 text-right">本期實收</th>
                    <th className="px-3 py-3 text-right">尾款待收</th>
                    <th className="px-3 py-3 text-right">服務紀錄</th>
                    <th className="px-3 py-3 text-right">回訪完成率</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#edf0ee]">
                  {report.LocationsSummary.map((row) => (
                    <tr key={row.LocationId}>
                      <td className="px-3 py-3 font-extrabold text-[#203d32]">{row.LocationName}</td>
                      <td className="px-3 py-3 text-right">{count(row.OrderCount)}</td>
                      <td className="px-3 py-3 text-right font-bold">{money(row.TotalAmount)}</td>
                      <td className="px-3 py-3 text-right">{money(row.PaymentReceived)}</td>
                      <td className="px-3 py-3 text-right">{money(row.BalanceAmount)}</td>
                      <td className="px-3 py-3 text-right">{count(row.ServiceRecordCount)}</td>
                      <td className="px-3 py-3 text-right">{row.FollowUpCompletionRate || 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className="grid gap-5 lg:grid-cols-2">
            <Breakdown title="訂單狀態" rows={report.OrderStatuses} amountKey="TotalAmount" />
            <Breakdown title="收款方式" rows={report.PaymentMethods} amountKey="Amount" />
          </div>
        </div>
      ) : null}
    </AdminLayout>
  );
}
